import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Comment } from './Comment';
import { CommentInput } from './CommentInput';
import { Comment as CommentType } from './CommentSection';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../../../components/Button';

interface CommentsModalProps {
  open: boolean;
  onClose: () => void;
  comments: CommentType[];
  tripId: string;
  storyId: string;
  linkSecret: string | null;
  isOwner: boolean;
}

export const CommentsModal = ({
  open,
  onClose,
  comments,
  tripId,
  storyId,
  linkSecret,
  isOwner,
}: CommentsModalProps) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full sm:translate-y-4 sm:opacity-0 sm:scale-95"
              enterTo="translate-y-0 sm:scale-100 sm:opacity-100"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0 sm:scale-100 sm:opacity-100"
              leaveTo="translate-y-full sm:translate-y-4 sm:opacity-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-t-xl sm:rounded-xl bg-surface text-left shadow-xl transition-all w-full sm:max-w-lg flex flex-col max-h-[85vh] sm:max-h-[80vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-default shrink-0">
                  <Dialog.Title className="text-base  leading-6 text-main">
                    Comments
                  </Dialog.Title>
                  <Button type="button" variant="ghost" size="sm" className="p-1" onClick={onClose}>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </Button>
                </div>

                {/* Comments List */}
                <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 min-h-[200px]">
                  {comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-muted py-8">
                      <p>No comments yet.</p>
                      <p className="text-sm">Start the conversation.</p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <Comment
                        key={comment.id}
                        id={comment.id}
                        name={comment.name}
                        userId={comment.userId}
                        body={comment.body}
                        timeText={formatDistanceToNow(new Date(comment.createdAt), {
                          addSuffix: true,
                        })}
                        tripId={tripId}
                        isOwner={isOwner}
                      />
                    ))
                  )}
                </div>

                {/* Footer (Input) */}
                <div className="border-t border-default px-4 py-2 bg-surface pb-6 sm:pb-2 shrink-0">
                  <CommentInput tripId={tripId} storyId={storyId} linkSecret={linkSecret} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
